import * as fabric from 'fabric'
import {
  TEXT_TYPES,
  TRANSFORM_TEXT_PROPS,
  getObjectBoundsInAreaSpace,
  getTransformState,
  getVisualObjectDimensionsInCm,
  normalizeAngle,
  normalizeTextScaleIntoFontSize,
  setObjectAngleAroundCenter,
  translateObjectInAreaSpace,
} from './fabric-editor-helpers'
import { syncHiddenColorsDataTransform } from './fabric-editor-hidden-colors'

/**
 * Logiche di modifica proprieta'/azioni oggetto (size/align/delete/property).
 */

export const removeFabricObject = ({
  canvas,
  object,
  loghiRefs,
}: {
  canvas: fabric.Canvas | null | undefined
  object: fabric.Object | null
  loghiRefs: { current: fabric.Object[] }
}) => {
  if (!canvas || !object) return false

  const removableObject = (object as any)?.customId ? object : null
  if (!removableObject) return false

  const removableId = (removableObject as any).customId

  canvas.discardActiveObject()
  canvas.remove(removableObject)
  canvas.requestRenderAll()

  loghiRefs.current = loghiRefs.current.filter((logo: any) => logo.customId !== removableId)

  return true
}

export const deleteFabricObjectById = ({
  canvas,
  id,
  loghiRefs,
}: {
  canvas: fabric.Canvas | null | undefined
  id: string | number
  loghiRefs: { current: fabric.Object[] }
}) => {
  if (!canvas) return

  const fabricObject = canvas.getObjects().find((obj: any) => obj.customId === id)
  if (fabricObject) {
    removeFabricObject({ canvas, object: fabricObject, loghiRefs })
  }
}

export const deleteActiveFabricObject = ({
  canvas,
  loghiRefs,
}: {
  canvas: fabric.Canvas | null | undefined
  loghiRefs: { current: fabric.Object[] }
}) => {
  if (!canvas) return false

  const activeObject = canvas.getActiveObject() as any
  if (!activeObject) return false

  if (TEXT_TYPES.includes(activeObject.type) && activeObject.isEditing) {
    return false
  }

  return removeFabricObject({ canvas, object: activeObject, loghiRefs })
}

export const alignFabricObject = ({
  canvas,
  areaStampa,
  id,
  alignment,
  onSyncFabricToState,
}: {
  canvas: fabric.Canvas | null | undefined
  areaStampa: fabric.Object | null | undefined
  id: string | number
  alignment: string
  onSyncFabricToState: (obj: fabric.Object) => void
}) => {
  if (!canvas || !areaStampa) return

  const fabricObject = canvas.getObjects().find((obj: any) => obj.customId === id)
  if (!fabricObject) return

  fabricObject.setCoords()
  areaStampa.setCoords()

  const { areaBounds, objectBounds } = getObjectBoundsInAreaSpace(fabricObject, areaStampa)

  const areaCenterH = (areaBounds.minX + areaBounds.maxX) / 2
  const areaCenterV = (areaBounds.minY + areaBounds.maxY) / 2
  const objCenterH = (objectBounds.minX + objectBounds.maxX) / 2
  const objCenterV = (objectBounds.minY + objectBounds.maxY) / 2

  let deltaX = 0
  let deltaY = 0

  switch (alignment) {
    case 'left':
      deltaX = areaBounds.minX - objectBounds.minX
      break
    case 'centerHorizontal':
      deltaX = areaCenterH - objCenterH
      break
    case 'right':
      deltaX = areaBounds.maxX - objectBounds.maxX
      break
    case 'top':
      deltaY = areaBounds.minY - objectBounds.minY
      break
    case 'centerVertical':
      deltaY = areaCenterV - objCenterV
      break
    case 'bottom':
      deltaY = areaBounds.maxY - objectBounds.maxY
      break
    default:
      return
  }

  translateObjectInAreaSpace(fabricObject, areaStampa, deltaX, deltaY)
  canvas.renderAll()
  // Se il logo ha colori nascosti, il loro payload serializzato deve seguire
  // anche le trasformazioni applicate da sidebar, non solo quelle drag canvas.
  void syncHiddenColorsDataTransform(fabricObject)
  onSyncFabricToState(fabricObject)
}

export const applyObjectPropertyChange = ({
  canvas,
  areaStampa,
  id,
  property,
  value,
  larghezzaAreaStampa,
  altezzaAreaStampa,
  preTransformStateRef,
  onPreserveBoundaries,
  onSyncFabricToState,
}: {
  canvas: fabric.Canvas | null | undefined
  areaStampa: fabric.Object | null | undefined
  id: string | number
  property: string
  value: any
  larghezzaAreaStampa: number
  altezzaAreaStampa: number
  preTransformStateRef: { current: Record<string, any> | null }
  onPreserveBoundaries: (options: any) => void
  onSyncFabricToState: (obj: fabric.Object) => void
}) => {
  if (!canvas || !areaStampa) return

  const fabricObject = canvas.getObjects().find((obj: any) => obj.customId === id)
  if (!fabricObject) return

  preTransformStateRef.current = getTransformState(fabricObject)

  if (property === 'width') {
    const currentVisualDimensions = getVisualObjectDimensionsInCm(
      fabricObject,
      areaStampa,
      larghezzaAreaStampa,
      altezzaAreaStampa
    )
    const nextWidth = parseFloat(value) || 0

    if (currentVisualDimensions.width > 0 && nextWidth > 0) {
      const scaleRatio = nextWidth / currentVisualDimensions.width
      fabricObject.set({
        scaleX: (fabricObject.scaleX || 1) * scaleRatio,
        scaleY: (fabricObject.scaleY || 1) * scaleRatio,
      })
    }
  } else if (property === 'height') {
    const currentVisualDimensions = getVisualObjectDimensionsInCm(
      fabricObject,
      areaStampa,
      larghezzaAreaStampa,
      altezzaAreaStampa
    )
    const nextHeight = parseFloat(value) || 0

    if (currentVisualDimensions.height > 0 && nextHeight > 0) {
      const scaleRatio = nextHeight / currentVisualDimensions.height
      fabricObject.set({
        scaleX: (fabricObject.scaleX || 1) * scaleRatio,
        scaleY: (fabricObject.scaleY || 1) * scaleRatio,
      })
    }
  } else {
    const currentAngle = normalizeAngle(fabricObject.angle || 0)

    if (property === 'fontSize' && TEXT_TYPES.includes(fabricObject.type)) {
      normalizeTextScaleIntoFontSize(fabricObject)
    }

    if (property === 'angle') {
      setObjectAngleAroundCenter(fabricObject, parseFloat(value) || 0)
    } else if (TRANSFORM_TEXT_PROPS.includes(property)) {
      const nextTextValue = property === 'fontSize' ? Math.max(1, parseFloat(value) || 1) : value

      fabricObject.set('angle', 0)
      fabricObject.setCoords()

      fabricObject.set(property, nextTextValue)
      fabricObject.set('angle', currentAngle)
    }
  }

  fabricObject.setCoords()
  onPreserveBoundaries({
    target: fabricObject,
    transform: {
      action: ['text', 'fontFamily', 'fontWeight', 'fontSize', 'fontStyle', 'underline'].includes(
        property
      )
        ? 'typing'
        : property === 'angle'
        ? 'rotating'
        : 'scaling',
    },
  })

  canvas.requestRenderAll()
  // Mantiene gli elementi nascosti coerenti con resize/rotazione/dimensioni
  // modificati tramite controlli numerici della sidebar.
  void syncHiddenColorsDataTransform(fabricObject)
  onSyncFabricToState(fabricObject)
}

export const dispatchObjectAction = ({
  id,
  action,
  payload,
  onProperty,
  onAlign,
  onDelete,
}: {
  id: string | number
  action: string
  payload: any
  onProperty: (id: string | number, field: string, value: any) => void
  onAlign: (id: string | number, alignment: string) => void
  onDelete: (id: string | number) => void
}) => {
  if (!id) return

  if (action === 'property') {
    const field = payload?.name ?? payload?.property
    if (!field) return
    onProperty(id, field, payload?.value)
    return
  }

  if (action === 'align') {
    const alignment = payload?.alignment ?? payload?.value
    if (!alignment) return
    onAlign(id, alignment)
    return
  }

  if (action === 'delete') {
    onDelete(id)
  }
}
